/**
 * Share of the page's content width a block occupies on each breakpoint (1 = the full content
 * column). Rows divide it by their column count for their children, so an image inside a column can
 * tell the browser how wide it really renders instead of claiming the whole viewport — PageSpeed
 * flagged 750px-wide downloads for images shown at ~400px (and 194px) inside 2–3 column rows.
 * Buckets match BlockRenderer: mobile ≤ 767px, tablet 768–1023px, desktop ≥ 1024px.
 */
export interface ImageSlot {
  mobile: number;
  tablet: number;
  desktop: number;
}

export const FULL_SLOT: ImageSlot = { mobile: 1, tablet: 1, desktop: 1 };

const clampFraction = (value: number) => (Number.isFinite(value) && value > 0 ? Math.min(1, value) : 1);

/** The slot a child of a row with these per-breakpoint column fractions gets. */
export function narrowSlot(parent: ImageSlot, fraction: ImageSlot): ImageSlot {
  return {
    mobile: parent.mobile * clampFraction(fraction.mobile),
    tablet: parent.tablet * clampFraction(fraction.tablet),
    desktop: parent.desktop * clampFraction(fraction.desktop),
  };
}

/** Content column cap of a regular (non full-width) page section — BlockRenderer's max-w-7xl. */
const CONTENT_MAX_PX = 1280;

/**
 * `sizes` for an image filling `slot` of the content column. Errs on the large side (ignores
 * gaps and paddings) — a slightly bigger file is fine, a blurry one is not.
 */
export function buildImageSizes(slot: ImageSlot, fullWidth = false): string {
  const vw = (fraction: number) => `${Math.max(1, Math.ceil(fraction * 100))}vw`;
  const desktop = fullWidth
    ? vw(slot.desktop)
    : `(max-width: ${CONTENT_MAX_PX - 1}px) ${vw(slot.desktop)}, ${Math.ceil(slot.desktop * CONTENT_MAX_PX)}px`;
  return `(max-width: 767px) ${vw(slot.mobile)}, (max-width: 1023px) ${vw(slot.tablet)}, ${desktop}`;
}

/**
 * Width share of one column of a builder row, per breakpoint — mirrors RowBlock's storefront grid
 * classes (stacked or 2/3-up on phones; 4-column rows show 2 (or 3) up below 1024px; custom
 * column widths apply from 768px). With custom widths the widest column is used for all children.
 */
export function rowColumnFraction({ columns, mobileColumns, mobileThreeUp = false, columnWidths }: {
  columns: number;
  mobileColumns: number;
  mobileThreeUp?: boolean;
  columnWidths?: number[];
}): ImageSlot {
  const cols = Math.max(1, Math.round(columns) || 1);
  const mobileCount = mobileThreeUp ? 3 : mobileColumns === 2 && cols > 1 ? 2 : 1;
  const tabletCount = cols === 4 ? (mobileThreeUp ? 3 : 2) : cols;
  const widest = columnWidths?.length ? Math.max(...columnWidths.map(Number).filter(Number.isFinite)) / 100 : null;
  return {
    mobile: 1 / mobileCount,
    tablet: widest && cols !== 4 ? widest : 1 / tabletCount,
    desktop: widest || 1 / cols,
  };
}
